// Measurement side of the mobile gallery collapse — see _gallery-collapse.scss
// for the effect itself.
//
// Nothing here runs per frame: the compositor drives the whole animation off
// the scroll offset, and this element only publishes the offsets that
// animation's range is expressed in. Without scroll-driven animation support
// it does nothing at all and the gallery stays as it is — plain, unpinned and
// full size.

// Fraction of its resting height the gallery keeps once fully collapsed.
// Mirrored by $collapsed-ratio in _gallery-collapse.scss.
const COLLAPSED_RATIO = 0.3333;

// Rendered inset left around the artwork once the gallery is fully collapsed.
// The media padding scales down with everything else, so the artwork is grown
// back out until only this much of it is left.
const COLLAPSED_MEDIA_PADDING = 4;

// Kept in step with the media queries wrapping the effect in the stylesheet.
const MEDIA_QUERIES = [
  "(max-width: 1023px)",
  "(prefers-reduced-motion: no-preference)",
];

const CUSTOM_PROPERTIES = [
  "--gallery-collapse-start",
  "--gallery-collapse-distance",
  "--gallery-collapsed-height",
  "--gallery-collapse-artwork-scale",
];

class GalleryCollapse extends HTMLElement {
  constructor() {
    super();

    this._onQueryChange = () => this._sync();
    this._onResize = () => this._measure();
  }

  connectedCallback() {
    if (!CSS.supports("animation-timeline: scroll()")) return;

    this.section = this.closest(".main-product");
    // The gallery box gets sized down to the collapsed strip, so its own height
    // can't be the thing that measurement is taken from — the artwork it
    // overflows with is what the whole effect is scaled against.
    this.media = this.querySelector("media-gallery");
    if (!this.section || !this.media) return;

    this.queries = MEDIA_QUERIES.map((query) => window.matchMedia(query));
    this.queries.forEach((query) =>
      query.addEventListener("change", this._onQueryChange),
    );

    this._sync();
  }

  disconnectedCallback() {
    this.queries?.forEach((query) =>
      query.removeEventListener("change", this._onQueryChange),
    );

    this._teardown();
  }

  /** Starts or stops the effect to match the media queries the CSS is gated on. */
  _sync() {
    this._teardown();

    if (!this.queries.every((query) => query.matches)) return;

    // Gates every rule in _gallery-collapse.scss, so it has to land before the
    // first measurement — the sticky offset that measurement reads comes from
    // those rules.
    this.dataset.collapseReady = "";

    // Both the gallery's own height and everything stacked above the section
    // feed the range, so remeasure on any reflow rather than only on resize.
    this.observer = new ResizeObserver(this._onResize);
    this.observer.observe(this.media);
    this.observer.observe(document.body);

    window.addEventListener("resize", this._onResize);
    // The header publishes its height in a frame of its own, and images above
    // the section can still settle after that.
    window.addEventListener("load", this._onResize);

    this._measure();
  }

  _teardown() {
    this.observer?.disconnect();
    this.observer = null;

    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("load", this._onResize);

    delete this.dataset.collapseReady;

    // Only clear the published measurements while this element is still the
    // live one. A variant change morphs in a replacement, and an element on its
    // way out tidying up after itself would wipe what its successor has just
    // written — the two run back to back inside the same replaceWith().
    if (!this.isConnected) return;

    CUSTOM_PROPERTIES.forEach((property) =>
      this.section?.style.removeProperty(property),
    );
  }

  _measure() {
    // Only transforms move during the collapse, so this stays the resting
    // height for the whole of it.
    const height = this.media.offsetHeight;

    // Nothing sensible to pin or shrink yet — drop back out of the effect
    // rather than leave the stylesheet gated in with no range to animate over.
    if (!height) {
      delete this.dataset.collapseReady;
      return;
    }

    this.dataset.collapseReady = "";

    // Resolves whatever the stylesheet pinned the gallery at, header height
    // included.
    const stickyTop = parseFloat(getComputedStyle(this).top) || 0;
    // The section is the nearest thing in flow that isn't itself sticky, and
    // the gallery sits flush against the top of it.
    const sectionTop = window.scrollY + this.section.getBoundingClientRect().top;

    const { style } = this.section;
    style.setProperty(
      "--gallery-collapse-start",
      `${Math.max(0, sectionTop - stickyTop)}px`,
    );
    style.setProperty(
      "--gallery-collapse-distance",
      `${height * (1 - COLLAPSED_RATIO)}px`,
    );
    style.setProperty(
      "--gallery-collapsed-height",
      `${height * COLLAPSED_RATIO}px`,
    );

    // Square artwork is the height-bound case the padding is designed around,
    // so both the size it rests at and the size it has to reach are heights.
    const padding = parseFloat(
      getComputedStyle(this.media).getPropertyValue("--media-padding"),
    );
    const artwork = height - padding * 2;
    const collapsedArtwork =
      height - (COLLAPSED_MEDIA_PADDING / COLLAPSED_RATIO) * 2;

    style.setProperty(
      "--gallery-collapse-artwork-scale",
      artwork > 0 ? collapsedArtwork / artwork : 1,
    );
  }
}

if (!customElements.get("gallery-collapse")) {
  customElements.define("gallery-collapse", GalleryCollapse);
}
